// Analytics surface copy — the maintainer-gated `/analytics` dashboard
// (analytics+postgres Phase 6). User-facing chrome text only; the contract it
// renders is content-free by construction, so there is no project/issue prose
// to localize here — only the labels around the numbers.
//
// The cost metric is now `totalCostUsd` across every metered surface
// (coding/pm/handoff/workflow, analytics+postgres Phase 9). It is derived from
// token counts, not an invoice, so the caveat is "best-effort", not "coding only".

import type { MsgSection } from "./types.js";

export const analytics: MsgSection = {
  // --- Page shell ---
  "analytics.title": { en: "Analytics", de: "Analytics" },
  "analytics.subtitle": {
    en: "Who uses Loom, how, and how much. Aggregate usage across the gated population.",
    de: "Wer Loom nutzt, wie und wie viel. Aggregierte Nutzung über die freigeschaltete Population.",
  },
  "analytics.back": { en: "Back", de: "Zurück" },

  // --- States ---
  "analytics.loading": { en: "Loading analytics…", de: "Analytics werden geladen…" },
  "analytics.denied.title": { en: "No access", de: "Kein Zugriff" },
  "analytics.denied.body": {
    en: "Your account isn't on the analytics viewer allowlist. Ask an administrator to add you.",
    de: "Dein Konto steht nicht auf der Analytics-Freigabeliste. Bitte eine Administratorin, dich hinzuzufügen.",
  },
  "analytics.error.title": { en: "Couldn't load analytics", de: "Analytics konnten nicht geladen werden" },
  "analytics.empty": {
    en: "No usage recorded in this window yet.",
    de: "In diesem Zeitraum wurde noch keine Nutzung erfasst.",
  },

  // --- KPI tiles ---
  "analytics.kpi.users": { en: "Active users", de: "Aktive Nutzer" },
  "analytics.kpi.sessions": { en: "Sessions", de: "Sessions" },
  "analytics.kpi.projects": { en: "Projects", de: "Projekte" },
  "analytics.kpi.modeMix": { en: "Mode mix", de: "Modus-Mix" },
  "analytics.kpi.modeMix.pm": { en: "PM", de: "PM" },
  "analytics.kpi.modeMix.coding": { en: "Coding", de: "Coding" },
  "analytics.kpi.cost": { en: "Total cost", de: "Gesamtkosten" },
  "analytics.kpi.cost.note": {
    en: "Across all surfaces — derived from token counts, best-effort.",
    de: "Über alle Flächen – aus Token-Zahlen abgeleitet, näherungsweise.",
  },

  // --- Per-surface cost split ---
  "analytics.cost.split.title": { en: "Cost by surface", de: "Kosten nach Fläche" },
  "analytics.cost.surface.coding": { en: "Coding", de: "Coding" },
  "analytics.cost.surface.pm": { en: "PM", de: "PM" },
  "analytics.cost.surface.handoff": { en: "Handoff", de: "Übergabe" },
  "analytics.cost.surface.workflow": { en: "Workflow", de: "Workflow" },

  // --- Timeseries ---
  "analytics.timeseries.title": { en: "Daily usage", de: "Tägliche Nutzung" },
  "analytics.timeseries.sessions": { en: "Sessions / day", de: "Sessions / Tag" },
  "analytics.timeseries.empty": { en: "No daily data in range.", de: "Keine Tagesdaten im Zeitraum." },
  "analytics.timeseries.allSuppressed": {
    en: "Daily counts are all below the reporting threshold (<5/day).",
    de: "Die Tageswerte liegen alle unter der Meldeschwelle (<5/Tag).",
  },

  // --- Per-model breakdown ---
  "analytics.models.title": { en: "By model", de: "Nach Modell" },
  "analytics.models.col.model": { en: "Model", de: "Modell" },
  "analytics.models.col.cost": { en: "Cost", de: "Kosten" },
  "analytics.models.col.source": { en: "Source", de: "Quelle" },
  // "priced" = derived from raw token counts via the price table. "repriced" =
  // the emitting CLI's own USD figure moved onto the rate Anthropic bills (the
  // CLI prices Sonnet 5 and 5.5 at the $3/$15 sticker, the billed rate is
  // $2/$10), with its figure kept alongside. "reported" = a CLI figure taken
  // verbatim — no writer emits it any more, kept so older rows still read.
  "analytics.models.source.reported": { en: "reported", de: "gemeldet" },
  "analytics.models.source.priced": { en: "priced", de: "berechnet" },
  "analytics.models.source.repriced": { en: "repriced", de: "umgerechnet" },
  "analytics.models.col.input": { en: "Tokens in", de: "Tokens ein" },
  "analytics.models.col.output": { en: "Tokens out", de: "Tokens aus" },
  "analytics.models.col.cacheRead": { en: "Cache read", de: "Cache gelesen" },
  "analytics.models.col.cacheWrite": { en: "Cache write", de: "Cache geschrieben" },
  "analytics.models.col.calls": { en: "Calls", de: "Aufrufe" },
  "analytics.models.empty": {
    en: "No model spend in range.",
    de: "Keine Modellkosten im Zeitraum.",
  },

  // --- As-billed reconciliation (Admin cost report vs metered ledger) ---
  "analytics.billing.title": {
    en: "Billing reconciliation (last 7 days)",
    de: "Abrechnungsabgleich (letzte 7 Tage)",
  },
  "analytics.billing.billed": {
    en: "Billed by Anthropic (whole organisation)",
    de: "Von Anthropic abgerechnet (gesamte Organisation)",
  },
  "analytics.billing.metered": {
    en: "Metered by Loom (this environment only)",
    de: "Von Loom gemessen (nur diese Umgebung)",
  },
  "analytics.billing.delta": { en: "Delta", de: "Abweichung" },
  "analytics.billing.note": {
    en: "These two numbers cover different things, so the delta is not an accuracy score. Billed is the Anthropic cost report for the entire organisation — every environment, every review app, and every developer laptop sharing the same API key. Metered is this environment's ledger alone. The cost report cannot be split by API key, so a large gap usually means traffic from somewhere else, not a metering leak here. Read a gap as a leak only once you have added up every environment's metered total. Second caveat: coding spend is day-bucketed by a session's last activity, so multi-day coding sessions skew the comparison.",
    de: "Die beiden Zahlen erfassen Unterschiedliches — die Abweichung ist deshalb kein Genauigkeitsmass. Abgerechnet ist der Anthropic-Kostenbericht für die gesamte Organisation: alle Umgebungen, alle Review-Apps und alle Entwicklerrechner, die denselben API-Schlüssel nutzen. Gemessen ist allein das Ledger dieser Umgebung. Der Kostenbericht lässt sich nicht nach API-Schlüssel aufteilen; eine grosse Lücke bedeutet daher meist Verkehr von anderswo, nicht ein Metering-Leck hier. Werte eine Lücke erst dann als Leck, wenn du die gemessenen Summen aller Umgebungen addiert hast. Zweiter Vorbehalt: Coding-Kosten werden dem Tag der letzten Aktivität einer Session zugeordnet — mehrtägige Coding-Sessions verzerren den Vergleich.",
  },
  "analytics.billing.nonToken": {
    en: "Non-token billed spend (web search, code execution):",
    de: "Abgerechnete Nicht-Token-Kosten (Websuche, Code-Ausführung):",
  },

  // --- Per-project breakdown ---
  "analytics.projects.title": { en: "By project", de: "Nach Projekt" },
  "analytics.projects.col.project": { en: "Project", de: "Projekt" },
  "analytics.projects.col.sessions": { en: "Sessions", de: "Sessions" },
  "analytics.projects.col.users": { en: "Users", de: "Nutzer" },
  "analytics.projects.col.cost": { en: "Cost", de: "Kosten" },
  // The part of the project's cost its own assigned key paid.
  "analytics.projects.col.ownKey": { en: "On its own key", de: "Auf eigenem Schlüssel" },

  // --- Per-key breakdown ---
  "analytics.keys.title": { en: "By key", de: "Nach Schlüssel" },
  "analytics.keys.col.key": { en: "Key id", de: "Schlüssel-ID" },
  "analytics.keys.loom": { en: "Loom's own keys", de: "Eigene Schlüssel von Loom" },

  // --- Named (per-user) drill-down ---
  "analytics.named.title": { en: "Named usage", de: "Namentliche Nutzung" },
  "analytics.named.scope": { en: "Scope", de: "Bereich" },
  "analytics.named.scope.all": { en: "All projects (org-wide)", de: "Alle Projekte (organisationsweit)" },
  "analytics.named.reveal": { en: "Reveal named usage", de: "Namentliche Nutzung anzeigen" },
  "analytics.named.hide": { en: "Hide names", de: "Namen verbergen" },
  // --- Freshness disclosure ---
  // These numbers come out of a batch projection, not a live read, so two
  // refetches inside one cron window are identical BY CONSTRUCTION. Say so
  // where the numbers are, or an unchanged body reads as a frozen ledger.
  // "covers activity up to" deliberately, never "last updated": the watermark
  // is the newest fact IN the projection, and an idle fleet has an old one.
  "analytics.projection.note": {
    en: "Snapshot, refreshed about every {minutes} min — covers activity up to {asOf}. Anything since then isn't in these numbers yet.",
    de: "Momentaufnahme, etwa alle {minutes} Min. aktualisiert – erfasst Aktivität bis {asOf}. Alles danach steckt noch nicht in diesen Zahlen.",
  },
  "analytics.projection.empty": {
    en: "Snapshot, refreshed about every {minutes} min — no activity recorded in it yet.",
    de: "Momentaufnahme, etwa alle {minutes} Min. aktualisiert – noch keine Aktivität darin erfasst.",
  },

  "analytics.named.caution": {
    en: "Revealing names is an audited action — the access is logged before any name loads.",
    de: "Das Anzeigen von Namen ist eine protokollierte Aktion – der Zugriff wird vor dem Laden eines Namens festgehalten.",
  },
  "analytics.named.loading": { en: "Loading names…", de: "Namen werden geladen…" },
  "analytics.named.notEnabled": {
    en: "Named usage isn't enabled in this environment yet.",
    de: "Namentliche Nutzung ist in dieser Umgebung noch nicht aktiviert.",
  },
  "analytics.named.needsProject": {
    en: "Named usage is scoped per project for your access level. Org-wide viewing isn't enabled for your account.",
    de: "Namentliche Nutzung ist für deine Zugriffsstufe pro Projekt begrenzt. Organisationsweite Ansicht ist für dein Konto nicht aktiviert.",
  },
  "analytics.named.forbidden": {
    en: "You aren't authorized to view named usage.",
    de: "Du bist nicht berechtigt, namentliche Nutzung zu sehen.",
  },
  "analytics.named.empty": { en: "No named rows in range.", de: "Keine namentlichen Zeilen im Zeitraum." },
  "analytics.named.col.user": { en: "User", de: "Nutzer" },
  "analytics.named.col.sessions": { en: "Sessions", de: "Sessions" },
  "analytics.named.col.cost": { en: "Cost", de: "Kosten" },
  "analytics.named.col.lastActive": { en: "Last active", de: "Zuletzt aktiv" },

  // --- Session drill-down (the action ledger) ---
  "analytics.sessions.title": { en: "Sessions", de: "Sessions" },
  "analytics.sessions.reveal": { en: "Reveal sessions", de: "Sessions anzeigen" },
  "analytics.sessions.hide": { en: "Hide sessions", de: "Sessions verbergen" },
  "analytics.sessions.loading": { en: "Loading sessions…", de: "Sessions werden geladen…" },
  "analytics.sessions.empty": { en: "No sessions in range.", de: "Keine Sessions im Zeitraum." },
  "analytics.sessions.col.session": { en: "Session", de: "Session" },
  "analytics.sessions.col.status": { en: "Status", de: "Status" },
  "analytics.sessions.col.actions": { en: "Actions", de: "Aktionen" },
  "analytics.sessions.timeline.loading": {
    en: "Loading timeline…",
    de: "Verlauf wird geladen…",
  },
  "analytics.sessions.timeline.empty": {
    en: "No actions captured for this session.",
    de: "Für diese Session wurden keine Aktionen erfasst.",
  },
  "analytics.sessions.timeline.col.time": { en: "Time", de: "Zeit" },
  "analytics.sessions.timeline.col.kind": { en: "Kind", de: "Art" },
  "analytics.sessions.timeline.col.name": { en: "Name", de: "Name" },
  "analytics.sessions.timeline.col.outcome": { en: "Outcome", de: "Ergebnis" },
  "analytics.sessions.timeline.col.duration": { en: "Duration", de: "Dauer" },

  // --- Recent sessions widget (compact, half-width; pipeline deep-link) ---
  "analytics.recent.title": { en: "Recent sessions", de: "Letzte Sessions" },
  "analytics.recent.reveal": { en: "Reveal recent sessions", de: "Letzte Sessions anzeigen" },
  "analytics.recent.hide": { en: "Hide", de: "Verbergen" },
  "analytics.recent.col.pipeline": { en: "Pipeline", de: "Pipeline" },
  "analytics.recent.pipeline.none": { en: "—", de: "—" },

  // --- Session identity (what a conversation WAS) ---
  // `unknown` is a REAL state, not a missing value: a session that was opened
  // and never took a user turn. A row whose `kind` is absent renders no badge
  // at all — see `labels.ts`.
  "analytics.session.kind.chat": { en: "Chat", de: "Chat" },
  "analytics.session.kind.issue": { en: "Refinement", de: "Verfeinerung" },
  "analytics.session.kind.implement": { en: "Implementation", de: "Umsetzung" },
  "analytics.session.kind.unknown": { en: "Not started", de: "Nicht begonnen" },

  // --- Action kinds (the timeline's "Kind" column) ---
  "analytics.action.kind.user_message": { en: "Message", de: "Nachricht" },
  "analytics.action.kind.tool_use": { en: "Tool", de: "Werkzeug" },
  "analytics.action.kind.workflow": { en: "Loom step", de: "Loom-Schritt" },
  "analytics.action.kind.lifecycle": { en: "Session state", de: "Session-Status" },
  "analytics.action.kind.apply": { en: "Issue change", de: "Issue-Änderung" },
  "analytics.action.kind.error": { en: "Error", de: "Fehler" },

  // --- Outcome tokens ---
  // Only the heads worth naming. An unmapped token is humanized rather than
  // hidden, and every cell keeps the exact token in its tooltip.
  "analytics.outcome.ok": { en: "Worked", de: "Erfolgreich" },
  "analytics.outcome.error": { en: "Failed", de: "Fehlgeschlagen" },
  "analytics.outcome.unavailable": { en: "Not available", de: "Nicht verfügbar" },
  "analytics.outcome.rejected": { en: "Rejected", de: "Abgelehnt" },
  "analytics.outcome.terminal": { en: "Ended the session", de: "Session beendet" },
  "analytics.outcome.recoverable": { en: "Recovered", de: "Behoben" },
  "analytics.outcome.proposed": { en: "Proposed", de: "Vorgeschlagen" },
  "analytics.outcome.no_split": { en: "Kept as one", de: "Nicht aufgeteilt" },
  "analytics.outcome.split_recommended": {
    en: "Split recommended",
    de: "Aufteilung empfohlen",
  },
  "analytics.outcome.single_ticket": { en: "One ticket", de: "Ein Ticket" },

  // --- Chat transcript (Tier-3, read-only) ---
  "analytics.transcript.link": { en: "Read chat", de: "Chat lesen" },
  "analytics.transcript.col.chat": { en: "Chat", de: "Chat" },
  "analytics.transcript.title": { en: "Chat transcript", de: "Chat-Protokoll" },
  "analytics.transcript.back": { en: "Back to analytics", de: "Zurück zu Analytics" },
  "analytics.transcript.loading": {
    en: "Loading the transcript…",
    de: "Protokoll wird geladen…",
  },
  // States. The three "no transcript" reasons are indistinguishable on the
  // server and the copy does not pretend otherwise.
  "analytics.transcript.notAvailable.title": {
    en: "No transcript for this session",
    de: "Kein Protokoll für diese Session",
  },
  "analytics.transcript.notAvailable.body": {
    en: "Loom keeps a chat for 30 days. This session ran before Loom kept chats, its 30 days have passed, or someone reset it.",
    de: "Loom bewahrt einen Chat 30 Tage auf. Diese Session lief, bevor Loom Chats aufbewahrte, ihre 30 Tage sind vorbei, oder jemand hat sie zurückgesetzt.",
  },
  "analytics.transcript.forbidden.title": { en: "No access", de: "Kein Zugriff" },
  "analytics.transcript.forbidden.body": {
    en: "Reading someone else's chat needs a separate grant. Ask an administrator.",
    de: "Den Chat einer anderen Person zu lesen erfordert eine eigene Freigabe. Bitte eine Administratorin darum.",
  },
  "analytics.transcript.error.title": {
    en: "Couldn't load the transcript",
    de: "Protokoll konnte nicht geladen werden",
  },
  "analytics.transcript.empty": {
    en: "The chat for this session holds nothing readable.",
    de: "Der Chat dieser Session enthält nichts Lesbares.",
  },
  // The read itself is the thing a reader should be conscious of.
  "analytics.transcript.notice": {
    en: "Read-only, and recorded. This is someone else's conversation; opening it was written to the access log.",
    de: "Nur lesen, und protokolliert. Dies ist das Gespräch einer anderen Person; das Öffnen wurde im Zugriffsprotokoll festgehalten.",
  },
  // What the projection left out, said on the page rather than in a doc.
  "analytics.transcript.scope": {
    en: "What was said, and which tools ran. Tool inputs and results, readiness cards and design sketches are not shown.",
    de: "Was gesagt wurde und welche Werkzeuge liefen. Werkzeug-Eingaben und -Ergebnisse, Readiness-Karten und Design-Skizzen werden nicht gezeigt.",
  },
  "analytics.transcript.counts": {
    en: "{entries} of {events} recorded events",
    de: "{entries} von {events} erfassten Ereignissen",
  },
  "analytics.transcript.capped": {
    en: "This session is long enough that Loom dropped its oldest events. The transcript starts mid-conversation.",
    de: "Diese Session ist lang genug, dass Loom ihre ältesten Ereignisse verworfen hat. Das Protokoll beginnt mitten im Gespräch.",
  },
  "analytics.transcript.speaker.user": { en: "Person", de: "Person" },
  "analytics.transcript.speaker.assistant": { en: "Loom", de: "Loom" },
  "analytics.transcript.speaker.tool": { en: "Tool", de: "Werkzeug" },
  "analytics.transcript.speaker.error": { en: "Error", de: "Fehler" },

  // --- Shared cell bands ---
  // Small-cell suppression: a cohort below the threshold comes across null.
  "analytics.cell.suppressed": { en: "<5", de: "<5" },
  "analytics.cell.user": { en: "User {id}", de: "Nutzer {id}" },
  "analytics.cell.project": { en: "Project {id}", de: "Projekt {id}" },
};
